import React, { FC, useState } from 'react'

import {
	Box,
	BoxProps,
	FormControl,
	InputAdornment,
	OutlinedInputProps,
} from '@mui/material'

import styled from 'styled-components'

import { pxToRem } from '../libs/style'

import IconButton from './buttons/IconButton'
import CloseIcon from './icons/CloseIcon'
import SearchIcon from './icons/SearchIcon'
import Input from './Input'
import theme from './theme'

const IconButtonNoPadding = styled(IconButton)`
	padding: 0;
`

const Outer = styled(FormControl)`
	.MuiOutlinedInput-root {
		border-radius: 0.75rem;
		background-color: ${theme.palette.common.white};
	}

	.MuiOutlinedInput-input {
		padding: ${theme.spacing(1.8)} ${theme.spacing(2)};

		&.MuiOutlinedInput-inputAdornedStart {
			padding-left: 0;
		}
	}

	.MuiInputLabel-outlined {
		margin-left: ${theme.spacing(3)};
		text-overflow: ellipsis;
		font-size: 1rem;
		font-style: normal;
		font-weight: 500;
		line-height: 140%; /* 1.225rem */
		letter-spacing: 0.0005rem;
	}

	.MuiInputLabel-root.MuiInputLabel-outlined.MuiInputLabel-shrink.MuiFormLabel-colorPrimary {
		margin-left: 0;
	}

	.MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined {
		padding-right: ${theme.spacing(3)};
	}
`
const CloseIconSmall = styled(CloseIcon)`
	color: ${theme.palette.secondary.main};
	height: ${pxToRem(12)}rem;
`

interface SearchboxProps
	extends Pick<OutlinedInputProps, 'label' | 'value' | 'onChange' | 'id'>,
		Omit<BoxProps, 'onChange'> {
	onReset: () => void
	onEnterKey?: (e: React.KeyboardEvent) => void
	setValue?: (term: string) => void
	placeholder?: string
	setValueOnBlur?: boolean
	fullWidth?: boolean
}

const Searchbox: FC<SearchboxProps> = ({
	className,
	id = 'searchInput',
	label,
	onChange,
	onEnterKey,
	onReset,
	placeholder,
	setValue,
	fullWidth,
	value,
	setValueOnBlur = false,
	...props
}) => {
	const [shrink, setShrink] = useState(false)

	return (
		<Box {...props}>
			<Outer className={className} fullWidth={fullWidth}>
				<Input
					className={className}
					variant="outlined"
					label={label}
					value={value}
					id={id}
					fullWidth={fullWidth}
					placeholder={placeholder}
					onChange={onChange}
					onFocus={e => {
						if (!value) {
							setShrink(true)
						}
					}}
					onBlur={e => {
						const term = (e.target as HTMLInputElement).value
						if (!term) {
							setShrink(false)
						}
						setValueOnBlur && setValue && setValue(term)
					}}
					onKeyUp={e => {
						const term = (e.target as HTMLInputElement).value
						const shouldTriggerCallbacks = e.keyCode === 13
						if (shouldTriggerCallbacks) {
							if (onEnterKey !== undefined) {
								onEnterKey(e)
							}
							if (setValue !== undefined) {
								setValue(term)
							}
						}
					}}
					InputLabelProps={{ shrink: !!value || shrink }}
					InputProps={{
						endAdornment: (
							<InputAdornment position="start">
								{value ? (
									<IconButtonNoPadding onClick={onReset}>
										<CloseIconSmall />
									</IconButtonNoPadding>
								) : (
									''
								)}
							</InputAdornment>
						),
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
			</Outer>
		</Box>
	)
}

export default Searchbox
